import { markDrillWriting } from "../fuxi/drill-handwriting.js";
import type { HandwritingReading } from "../fuxi/handwriting-verdict.js";
import type { HandwritingSource } from "./handwriting-source.js";
import {
  type StrokePoint,
  type StrokePoints,
  characterShape,
} from "../handwriting/stroke-shape.js";
import {
  type StrokeStepVerdict,
  markStrokeStep,
} from "../handwriting/stroke-step.js";

/**
 * Marking what a reader writes in the drill, a stroke at a time and whole.
 *
 * The first three stages mark each stroke as it lands, by `stroke-step.ts`.
 * The last marks the character whole by `markDrillWriting`, which is the 复习
 * card's marking with one allowance, so a character the card would take is
 * one the drill takes. The index is fetched the first time that is asked
 * and kept by the source, which is what keeps it off the page of a reader who
 * traces and leaves.
 *
 * **The whole marking is behind a {@link CharacterMarker}**, so the drill
 * takes whichever it is given. The public page marks here, in the browser,
 * over the fetched index. The handler's page posts the ink and the handler
 * marks it, through `lianxi-recorder.ts`, and the drill sees no difference.
 */

/**
 * Mark the stroke that just landed against the stroke wanted next.
 *
 * @param properties - The character's strokes in the square, which is wanted,
 * the ink in CSS pixels and the side of the square in the same pixels.
 * @returns What the stroke was a go at.
 */
export function checkStroke(properties: {
  readonly taught: readonly StrokePoints[];
  readonly next: number;
  readonly ink: StrokePoints;
  readonly side: number;
}): StrokeStepVerdict {
  const { taught, next, ink, side } = properties;

  return markStrokeStep({
    taught,
    next,
    stroke: ink.map(([x, y]): StrokePoint => [x / side, y / side]),
  });
}

/** What became of a character written from memory. */
export interface CheckedCharacter {
  /** What the site made of the ink. */
  readonly reading: HandwritingReading;

  /**
   * Whether the pass is passed. 对 passes, and so does the right character
   * ranked second. See `src/fuxi/drill-handwriting.ts`.
   */
  readonly passed: boolean;

  /**
   * Whether anything was marked at all.
   *
   * A blank square and a character the index has no strokes for are neither
   * passed nor missed. The ink stays and the reader is told.
   */
  readonly marked: boolean;

  /**
   * What to say instead of the reading, where the marking has something of
   * its own to say. A marker that could not reach the handler says so here.
   */
  readonly line?: string;
}

/**
 * How a marking was asked for. `pressed` is the reader pressing Check, and
 * `automatic` is the drill asking on its own once the ink has as many strokes
 * as the character. The drill acts on an automatic marking only where it
 * passes, and a marker that keeps the outcome keeps nothing else of it.
 */
export type MarkingAsked = "pressed" | "automatic";

/** Marks a character written from memory, wherever the marking is done. */
export type CharacterMarker = (
  character: string,
  ink: readonly StrokePoints[],
  asked: MarkingAsked,
) => Promise<CheckedCharacter>;

/**
 * The marker that marks in the browser, over the index the source fetches.
 * @param source - Where the handwriting index is.
 * @returns The marker.
 */
export function localMarker(
  source: Pick<HandwritingSource, "index">,
): CharacterMarker {
  return async (character, ink) => checkCharacter({ character, ink, source });
}

/**
 * Mark the ink on the square against the character.
 *
 * @param properties - The character, the ink in CSS pixels, and the index.
 * @returns What the ink was, and whether the pass is passed.
 */
export async function checkCharacter(properties: {
  readonly character: string;
  readonly ink: readonly StrokePoints[];
  readonly source: Pick<HandwritingSource, "index">;
}): Promise<CheckedCharacter> {
  const { character, ink, source } = properties;

  return markDrillWriting({
    expected: character,
    shape: characterShape(ink),
    index: await source.index(),
  });
}
